'use client';

import {
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ExpandedState,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type TableState,
  type Updater,
  type ColumnVisibilityState,
  useTable
} from '@tanstack/react-table';
import {
  type Parser,
  type UseQueryStateOptions,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates
} from 'nuqs';
import { useLocale } from 'next-intl';
import * as React from 'react';

import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { normalizeColumnPinning } from '@/lib/data-table';
import { getSortingStateParser } from '@/lib/parsers';
import { appTableFeatures, type AppFeatures } from '@/lib/table-features';
import type { ExtendedColumnSort } from '@/types/data-table';

const PAGE_KEY = 'page';
const PER_PAGE_KEY = 'perPage';
const SORT_KEY = 'sort';
const ARRAY_SEPARATOR = ',';
const DEBOUNCE_MS = 300;
const THROTTLE_MS = 50;

interface UseDataTableProps<TData extends RowData>
  extends
    Omit<
      TableOptions<AppFeatures, TData>,
      | 'features'
      | 'state'
      | 'pageCount'
      | 'manualFiltering'
      | 'manualPagination'
      | 'manualSorting'
    >,
    Required<Pick<TableOptions<AppFeatures, TData>, 'pageCount'>> {
  initialState?: Omit<Partial<TableState<AppFeatures>>, 'sorting'> & {
    sorting?: ExtendedColumnSort<TData>[];
  };
  history?: 'push' | 'replace';
  debounceMs?: number;
  throttleMs?: number;
  clearOnDefault?: boolean;
  enableAdvancedFilter?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  startTransition?: React.TransitionStartFunction;
  /** When provided, column visibility is persisted to localStorage under this key */
  pageKey?: string;
  /** When provided, prefixes the page/perPage/sort URL keys to isolate state across multiple tables on the same URL (e.g. tabbed layouts) */
  urlNamespace?: string;
  /** Enable interactive column pinning (start/end) */
  enableColumnPinning?: boolean;
  /** Enable drag-and-drop column reordering */
  enableColumnOrdering?: boolean;
  /** Enable interactive column resizing */
  enableColumnResizing?: boolean;
  /** Enable row expansion */
  enableExpanding?: boolean;
}

export function useDataTable<TData extends RowData>(
  props: UseDataTableProps<TData>
) {
  const {
    columns,
    pageCount = -1,
    initialState,
    history = 'replace',
    debounceMs = DEBOUNCE_MS,
    throttleMs = THROTTLE_MS,
    clearOnDefault = false,
    enableAdvancedFilter = false,
    scroll = false,
    shallow = true,
    startTransition,
    pageKey,
    urlNamespace,
    enableColumnPinning = true,
    enableColumnOrdering: _enableColumnOrdering = true,
    enableColumnResizing = true,
    enableExpanding = true,
    ...tableProps
  } = props;

  const locale = useLocale();

  const pageUrlKey = urlNamespace ? `${urlNamespace}_page` : PAGE_KEY;
  const perPageUrlKey = urlNamespace ? `${urlNamespace}_perPage` : PER_PAGE_KEY;
  const sortUrlKey = urlNamespace ? `${urlNamespace}_sort` : SORT_KEY;

  const queryStateOptions = React.useMemo<
    Omit<UseQueryStateOptions<string>, 'parse'>
  >(
    () => ({
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition
    }),
    [
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition
    ]
  );

  // Filter parsers use debounceMs:0 — the useDebouncedCallback below provides
  // the only debounce, preventing a double-debounce (~600ms total delay).
  const filterQueryStateOptions = React.useMemo<
    Omit<UseQueryStateOptions<string>, 'parse'>
  >(
    () => ({
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs: 0,
      clearOnDefault,
      startTransition
    }),
    [history, scroll, shallow, throttleMs, clearOnDefault, startTransition]
  );

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(
    initialState?.rowSelection ?? {}
  );
  const columnVisibilityStorageKey = pageKey
    ? `column-visibility-${pageKey}`
    : null;

  // Auto-hide filter-only columns (size: 0) so they don't render as empty cells
  const filterOnlyHidden = React.useMemo(() => {
    const hidden: ColumnVisibilityState = {};
    for (const col of columns) {
      if (col.size === 0 && col.id) {
        hidden[col.id] = false;
      }
    }
    return hidden;
  }, [columns]);

  const [columnVisibility, setColumnVisibilityRaw] =
    React.useState<ColumnVisibilityState>(() => {
      let base: ColumnVisibilityState = {};
      if (columnVisibilityStorageKey && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem(columnVisibilityStorageKey);
          if (stored) base = JSON.parse(stored) as ColumnVisibilityState;
        } catch {
          // Ignore corrupted data
        }
      }
      return {
        ...filterOnlyHidden,
        ...(initialState?.columnVisibility ?? {}),
        ...base
      };
    });

  const setColumnVisibility = React.useCallback(
    (updaterOrValue: React.SetStateAction<ColumnVisibilityState>) => {
      setColumnVisibilityRaw((prev) => {
        const next =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(prev)
            : updaterOrValue;
        if (columnVisibilityStorageKey) {
          try {
            localStorage.setItem(
              columnVisibilityStorageKey,
              JSON.stringify(next)
            );
          } catch {
            // Ignore quota errors
          }
        }
        return next;
      });
    },
    [columnVisibilityStorageKey]
  );

  // Column pinning — persisted to localStorage
  const columnPinningStorageKey = pageKey ? `column-pinning-${pageKey}` : null;

  const hasSelectColumn = columns.some((col) => col.id === 'select');

  const [columnPinning, setColumnPinningRaw] =
    React.useState<ColumnPinningState>(() => {
      let base: ColumnPinningState = normalizeColumnPinning(
        initialState?.columnPinning
      );
      if (columnPinningStorageKey && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem(columnPinningStorageKey);
          // Migrates a pre-v9 `{left,right}` blob (and hardens against a
          // malformed one) to `{start,end}` — see normalizeColumnPinning.
          if (stored) base = normalizeColumnPinning(JSON.parse(stored));
        } catch {
          // Ignore corrupted data
        }
      }
      // Always pin select column to the logical start
      if (hasSelectColumn && !base.start.includes('select')) {
        base = { ...base, start: ['select', ...base.start] };
      }
      return base;
    });

  const setColumnPinning = React.useCallback(
    (updaterOrValue: React.SetStateAction<ColumnPinningState>) => {
      setColumnPinningRaw((prev) => {
        let next =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(prev)
            : updaterOrValue;
        // Always keep select column pinned to the logical start
        if (hasSelectColumn && !next.start?.includes('select')) {
          next = { ...next, start: ['select', ...(next.start ?? [])] };
        }
        if (columnPinningStorageKey) {
          try {
            localStorage.setItem(columnPinningStorageKey, JSON.stringify(next));
          } catch {
            // Ignore quota errors
          }
        }
        return next;
      });
    },
    [columnPinningStorageKey, hasSelectColumn]
  );

  // Column order — persisted to localStorage
  const columnOrderStorageKey = pageKey ? `column-order-${pageKey}` : null;

  const [columnOrder, setColumnOrderRaw] = React.useState<ColumnOrderState>(
    () => {
      if (columnOrderStorageKey && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem(columnOrderStorageKey);
          if (stored) return JSON.parse(stored) as ColumnOrderState;
        } catch {
          // Ignore corrupted data
        }
      }
      return initialState?.columnOrder ?? [];
    }
  );

  const setColumnOrder = React.useCallback(
    (updaterOrValue: React.SetStateAction<ColumnOrderState>) => {
      setColumnOrderRaw((prev) => {
        const next =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(prev)
            : updaterOrValue;
        if (columnOrderStorageKey) {
          try {
            localStorage.setItem(columnOrderStorageKey, JSON.stringify(next));
          } catch {
            // Ignore quota errors
          }
        }
        return next;
      });
    },
    [columnOrderStorageKey]
  );

  // Column sizing — persisted to localStorage
  const columnSizingStorageKey = pageKey ? `column-sizing-${pageKey}` : null;

  const [columnSizing, setColumnSizingRaw] = React.useState<ColumnSizingState>(
    () => {
      let base: ColumnSizingState = initialState?.columnSizing ?? {};
      if (columnSizingStorageKey && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem(columnSizingStorageKey);
          if (stored) base = JSON.parse(stored) as ColumnSizingState;
        } catch {
          // Ignore corrupted data
        }
      }
      // Set proper default sizes for fixed columns so pinned offsets are correct
      if (hasSelectColumn && !base.select) {
        base = { ...base, select: 40 };
      }
      return base;
    }
  );

  const setColumnSizing = React.useCallback(
    (updaterOrValue: React.SetStateAction<ColumnSizingState>) => {
      setColumnSizingRaw((prev) => {
        const next =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(prev)
            : updaterOrValue;
        if (columnSizingStorageKey) {
          try {
            localStorage.setItem(columnSizingStorageKey, JSON.stringify(next));
          } catch {
            // Ignore quota errors
          }
        }
        return next;
      });
    },
    [columnSizingStorageKey]
  );

  // Row expansion — in-memory only
  const [expanded, setExpanded] = React.useState<ExpandedState>(
    initialState?.expanded ?? {}
  );

  // Reset all persisted table state to defaults
  const resetTableState = React.useCallback(() => {
    if (columnVisibilityStorageKey) {
      localStorage.removeItem(columnVisibilityStorageKey);
    }
    if (columnPinningStorageKey) {
      localStorage.removeItem(columnPinningStorageKey);
    }
    if (columnOrderStorageKey) {
      localStorage.removeItem(columnOrderStorageKey);
    }
    if (columnSizingStorageKey) {
      localStorage.removeItem(columnSizingStorageKey);
    }
    setColumnVisibilityRaw(initialState?.columnVisibility ?? {});
    setColumnPinningRaw(initialState?.columnPinning ?? { start: [], end: [] });
    setColumnOrderRaw(initialState?.columnOrder ?? []);
    setColumnSizingRaw(initialState?.columnSizing ?? {});
    setExpanded(initialState?.expanded ?? {});
  }, [
    columnVisibilityStorageKey,
    columnPinningStorageKey,
    columnOrderStorageKey,
    columnSizingStorageKey,
    initialState?.columnVisibility,
    initialState?.columnPinning,
    initialState?.columnOrder,
    initialState?.columnSizing,
    initialState?.expanded
  ]);

  const [page, setPage] = useQueryState(
    pageUrlKey,
    parseAsInteger.withOptions(queryStateOptions).withDefault(1)
  );
  const [perPage, setPerPage] = useQueryState(
    perPageUrlKey,
    parseAsInteger
      .withOptions(queryStateOptions)
      .withDefault(initialState?.pagination?.pageSize ?? 10)
  );

  const pagination: PaginationState = React.useMemo(() => {
    return {
      pageIndex: page - 1, // zero-based index -> one-based index
      pageSize: perPage
    };
  }, [page, perPage]);

  // Use a ref so back-to-back calls (e.g. setPageSize + setPageIndex)
  // always read the latest values instead of a stale memoized snapshot.
  const paginationRef = React.useRef(pagination);
  paginationRef.current = pagination;

  const onPaginationChange = React.useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      const prev = paginationRef.current;
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(prev)
          : updaterOrValue;
      paginationRef.current = next;
      void setPage(next.pageIndex + 1);
      void setPerPage(next.pageSize);
    },
    [setPage, setPerPage]
  );

  const columnIds = React.useMemo(() => {
    return new Set(
      columns.map((column) => column.id).filter(Boolean) as string[]
    );
  }, [columns]);

  const [sorting, setSorting] = useQueryState(
    sortUrlKey,
    getSortingStateParser<TData>(columnIds)
      .withOptions(queryStateOptions)
      .withDefault(initialState?.sorting ?? [])
  );

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(sorting);
        setSorting(newSorting as ExtendedColumnSort<TData>[]);
      } else {
        setSorting(updaterOrValue as ExtendedColumnSort<TData>[]);
      }
    },
    [sorting, setSorting]
  );

  const filterableColumns = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return columns.filter((column) => column.enableColumnFilter);
  }, [columns, enableAdvancedFilter]);

  const filterParsers = React.useMemo(() => {
    if (enableAdvancedFilter) return {};

    return filterableColumns.reduce<
      Record<string, Parser<string> | Parser<string[]>>
    >((acc, column) => {
      if (
        column.meta?.options ||
        column.meta?.variant === 'text' ||
        column.meta?.variant === 'infiniteMultiSelect' ||
        column.meta?.variant === 'date' ||
        column.meta?.variant === 'dateRange' ||
        column.meta?.variant === 'dateTimeRange'
      ) {
        acc[column.id ?? ''] = parseAsArrayOf(
          parseAsString,
          ARRAY_SEPARATOR
        ).withOptions(filterQueryStateOptions);
      } else {
        acc[column.id ?? ''] = parseAsString.withOptions(
          filterQueryStateOptions
        );
      }
      return acc;
    }, {});
  }, [filterableColumns, filterQueryStateOptions, enableAdvancedFilter]);

  const [filterValues, setFilterValues] = useQueryStates(filterParsers);

  const [isFilterPending, setIsFilterPending] = React.useState(false);

  const debouncedSetFilterValues = useDebouncedCallback(
    (values: typeof filterValues) => {
      setIsFilterPending(false);
      void setPage(1);
      void setFilterValues(values);
    },
    debounceMs
  );

  const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return Object.entries(filterValues).reduce<ColumnFiltersState>(
      (filters, [key, value]) => {
        if (value !== null) {
          const processedValue = Array.isArray(value)
            ? value
            : typeof value === 'string' && /[^a-zA-Z0-9]/.test(value)
              ? value.split(/[^a-zA-Z0-9]+/).filter(Boolean)
              : [value];

          filters.push({
            id: key,
            value: processedValue
          });
        }
        return filters;
      },
      []
    );
  }, [filterValues, enableAdvancedFilter]);

  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(initialColumnFilters);

  const onColumnFiltersChange = React.useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      if (enableAdvancedFilter) return;

      setColumnFilters((prev) => {
        const next =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(prev)
            : updaterOrValue;

        const filterUpdates = next.reduce<
          Record<string, string | string[] | null>
        >((acc, filter) => {
          if (filterableColumns.find((column) => column.id === filter.id)) {
            acc[filter.id] = filter.value as string | string[];
          }
          return acc;
        }, {});

        for (const prevFilter of prev) {
          if (!next.some((filter) => filter.id === prevFilter.id)) {
            filterUpdates[prevFilter.id] = null;
          }
        }

        setIsFilterPending(true);
        debouncedSetFilterValues(filterUpdates);
        return next;
      });
    },
    [debouncedSetFilterValues, filterableColumns, enableAdvancedFilter]
  );

  const table = useTable({
    ...tableProps,
    features: appTableFeatures,
    columns,
    initialState,
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      columnPinning,
      columnOrder,
      columnSizing,
      expanded
    },
    defaultColumn: {
      ...tableProps.defaultColumn,
      enableColumnFilter: false
    },
    enableRowSelection: true,
    enableColumnPinning,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    columnResizeDirection: locale === 'ar' ? 'rtl' : 'ltr',
    enableExpanding,
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onExpandedChange: setExpanded,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    meta: {
      ...tableProps.meta,
      isFilterPending,
      resetTableState
    }
  });

  return { table, shallow, debounceMs, throttleMs, isFilterPending };
}
